import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { FiEdit } from "react-icons/fi";
import { AiOutlineDelete } from "react-icons/ai";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  createFeedbackMCQQuestion,
  deleteFeedbackMCQQuestion,
  getFeedBackQuestions,
  updateFeedbackMCQQuestion,
} from "../../../../redux/slices/feedbackSlice";

const MCQquestions = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [questionId, setQuestionId] = useState(null);
  const [data, setData] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    question: "",
    options: ["", "", "", ""],
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const { feedbackQuestions, refetch } = useSelector(
    (state) => state.feedbackSlice
  );

  const prevProps = useRef({ refetch }).current;

  useEffect(() => {
    dispatch(getFeedBackQuestions(setIsLoading));
  }, []);

  useEffect(() => {
    if (prevProps.refetch !== refetch) {
      dispatch(getFeedBackQuestions(setIsLoading));
    }
    return () => {
      prevProps.refetch = refetch;
    };
  }, [refetch]);

  const columns = [
    {
      name: "Question",
      selector: (row) => row.question,
      sortable: true,
      grow: 2,
    },
    {
      name: "Options",
      selector: (row) => row.options,
      cell: (row) => (
        <div>
          {row.options.map((opt, index) => (
            <div key={index}>
              {index + 1}. {opt}
            </div>
          ))}
        </div>
      ),
      grow: 2,
    },
    {
      name: "Status",
      selector: (row) => row.active,
      sortable: true,
      cell: (row) => (
        <span
          className={`badge ${
            row.active === true ? "bg-success" : "bg-danger"
          }`}
        >
          {row.active === true ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Created At",
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-link p-0"
            onClick={() => handleEdit(row)}
            style={{ color: "#007bff" }}
          >
            <FiEdit size={18} />
          </button>
          <button
            className="btn btn-link p-0"
            onClick={() => handleDelete(row._id)}
            style={{ color: "#dc3545" }}
          >
            <AiOutlineDelete size={18} />
          </button>
        </div>
      ),
      center: true,
    },
  ];

  useEffect(() => {
    if (feedbackQuestions?.mcqQuestion) {
      const sortedData = [...feedbackQuestions.mcqQuestion].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setData(sortedData);
    }
  }, [feedbackQuestions]);

  const handleEdit = (row) => {
    setIsEditing(true);
    setNewQuestion({
      question: row.question,
      options: row.options.map((opt) => opt),
    });
    setQuestionId(row._id);
    setShowModal(true);
  };

  const handleDelete = (question) => {
    setSelectedQuestion(question);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteFeedbackMCQQuestion(selectedQuestion, setIsLoading));
    setShowDeleteModal(false);
    setSelectedQuestion(null);
  };

  const handleAddQuestion = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setNewQuestion({
      question: "",
      options: ["", "", "", ""],
    });
  };

  const handleSubmit = () => {
    const questionData = {
      question: newQuestion.question,
      options: newQuestion.options.filter((opt) => opt.trim() !== ""),
    };

    if (isEditing) {
      dispatch(
        updateFeedbackMCQQuestion(questionId, questionData, setIsLoading)
      );
    } else {
      dispatch(createFeedbackMCQQuestion(questionData, setIsLoading));
    }
    handleCloseModal();
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title">MCQ Questions</h3>
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-outline-warning"
            onClick={handleAddQuestion}
          >
            Add Question
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body py-4">
              <DataTable
                columns={columns}
                data={data}
                pagination
                paginationPerPage={20}
                paginationRowsPerPageOptions={[
                  10, 20, 30, 50, 100, 200, 500,
                ]}
                highlightOnHover
                pointerOnHover
                responsive
                striped
                subHeader
                subHeaderAlign="left"
                subHeaderComponent={<></>}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Question Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit MCQ Question" : "Add MCQ Question"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className="form-group mb-3">
              <label htmlFor="question" className="form-label">
                Question
              </label>
              <input
                type="text"
                className="form-control"
                id="question"
                placeholder="Enter your question"
                value={newQuestion.question}
                onChange={(e) =>
                  setNewQuestion({
                    ...newQuestion,
                    question: e.target.value,
                  })
                }
              />
            </div>
            <div className="form-group">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">Options</label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    setNewQuestion({
                      ...newQuestion,
                      options: [...newQuestion.options, ""],
                    });
                  }}
                >
                  + Add More Options
                </button>
              </div>
              {newQuestion.options.map((option, index) => (
                <div key={index} className="mb-2 input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.options];
                      newOptions[index] = e.target.value;
                      setNewQuestion({
                        ...newQuestion,
                        options: newOptions,
                      });
                    }}
                  />
                  {newQuestion.options.length > 2 && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        const newOptions = newQuestion.options.filter(
                          (_, i) => i !== index
                        );
                        setNewQuestion({
                          ...newQuestion,
                          options: newOptions,
                        });
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn_secondary" onClick={handleCloseModal}>
            Cancel
          </button>
          <button
            className="btn btn_primary"
            onClick={handleSubmit}
            disabled={
              !newQuestion.question || newQuestion.options.some((opt) => !opt)
            }
          >
            {isEditing ? "Update" : "Add"} Question
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this question?</p>
          <p className="text-muted small mb-0">{selectedQuestion?.question}</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn_secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleConfirmDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MCQquestions;
